/* ==========================================================================
   Kynvera theme JS
   --------------------------------------------------------------------------
   One file, no framework. Every controller is idempotent and re-runs on
   `shopify:section:load` so the theme editor doesn't break behaviour when a
   section reloads — the bug the old header had.

   Controllers
     1. Scroll reveal      [data-kv-reveal]
     2. AJAX add to cart   [data-add-to-cart]
     3. Sticky header      [data-kv-header]
     4. Mega menu          [data-kv-mega-toggle] / [data-kv-mega]
     5. Mobile drawer      [data-kv-drawer-open] / [data-kv-drawer]
     6. Quantity stepper   [data-kv-qty]
     7. Product page       [data-kv-product]
     8. Announcement bar   [data-kv-announce]
     9. Hero carousel      [data-kv-hero]
    10. Countdown          [data-kv-countdown]
    11. Wishlist           [data-kv-wishlist]
    12. Wishlist page      [data-kv-wishlist-page]
   ========================================================================== */

(function () {
  'use strict';

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Utilities
     ---------------------------------------------------------------------- */

  function once(el, key) {
    if (!el || el.dataset[key] === 'true') return false;
    el.dataset[key] = 'true';
    return true;
  }

  function formatMoney(cents) {
    // Shopify exposes the shop's money format on window when available; fall
    // back to a plain decimal so we never render "undefined".
    try {
      return (cents / 100).toFixed(2);
    } catch (e) {
      return '';
    }
  }

  /* Focus trap — used by both the drawer and the mega panel
     ---------------------------------------------------------------------- */

  var FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled])',
    'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function trapFocus(container, event) {
    var items = Array.prototype.filter.call(
      container.querySelectorAll(FOCUSABLE),
      function (el) { return el.offsetParent !== null; }
    );
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* Body scroll lock. A class + preserved scroll position, not an inline
     `overflow: hidden`, which fails on iOS Safari.
     ---------------------------------------------------------------------- */

  var scrollY = 0;

  function lockScroll() {
    scrollY = window.scrollY;
    document.body.style.top = '-' + scrollY + 'px';
    document.body.classList.add('kv-scroll-locked');
  }

  function unlockScroll() {
    document.body.classList.remove('kv-scroll-locked');
    document.body.style.top = '';
    window.scrollTo(0, scrollY);
  }


  /* 1. Scroll reveal
     ---------------------------------------------------------------------- */

  function initReveal(root) {
    // The marker lives on its own attribute. It used to be written back onto
    // data-kv-reveal itself, which clobbered the stagger value the observer
    // reads on intersection — every delay parsed as NaN and fell back to 0, so
    // nothing was ever actually staggered. The old :not() guard was inert too:
    // it tested data-kvReveal, an attribute nothing sets.
    var targets = (root || document).querySelectorAll('[data-kv-reveal]:not([data-kv-reveal-bound])');
    if (!targets.length) return;

    if (REDUCED_MOTION || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(targets, function (el) {
        el.setAttribute('data-kv-reveal-bound', '');
        el.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var delay = parseInt(el.getAttribute('data-kv-reveal'), 10) || 0;
        setTimeout(function () { el.classList.add('is-visible'); }, delay);
        observer.unobserve(el);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });

    Array.prototype.forEach.call(targets, function (el) {
      el.setAttribute('data-kv-reveal-bound', '');
      observer.observe(el);
    });
  }


  /* 2. AJAX add to cart
     --------------------------------------------------------------------------
     Bound once at document level, so any card anywhere — homepage, collection,
     search — works identically. The old theme bound this inside one section.
     ---------------------------------------------------------------------- */

  function updateCartCount(count) {
    var nodes = document.querySelectorAll('[data-kv-cart-count]');
    Array.prototype.forEach.call(nodes, function (node) {
      node.textContent = count;
      node.hidden = count < 1;
    });
  }

  function refreshCartCount() {
    fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) { if (cart) updateCartCount(cart.item_count); })
      .catch(function () { /* count is cosmetic — never block on it */ });
  }

  function handleAddToCart(button) {
    var variantId = button.getAttribute('data-variant-id');
    if (!variantId || button.disabled) return;

    var original = button.innerHTML;
    var label = button.getAttribute('aria-label') || '';

    button.disabled = true;
    button.classList.remove('is-error', 'is-success');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    })
      .then(function (response) {
        if (!response.ok) return response.json().then(function (e) { throw e; });
        return response.json();
      })
      .then(function () {
        button.classList.add('is-success');
        button.setAttribute('aria-label', 'Added to cart');
        button.innerHTML =
          '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
        refreshCartCount();
      })
      .catch(function (error) {
        button.classList.add('is-error');
        button.setAttribute('aria-label', (error && error.description) || 'Could not add to cart');
        button.innerHTML =
          '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
      })
      .then(function () {
        setTimeout(function () {
          button.disabled = false;
          button.classList.remove('is-error', 'is-success');
          button.innerHTML = original;
          button.setAttribute('aria-label', label);
        }, 1800);
      });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-add-to-cart]');
    if (!button) return;
    event.preventDefault();
    handleAddToCart(button);
  });


  /* 3. Sticky header
     ---------------------------------------------------------------------- */

  function initHeader(root) {
    var header = (root || document).querySelector('[data-kv-header]');
    if (!header || !once(header, 'kvHeader')) return;

    var ticking = false;

    function update() {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }, { passive: true });

    update();
  }


  /* 4. Mega menu
     ---------------------------------------------------------------------- */

  function initMega(root) {
    var scope = root || document;
    var toggle = scope.querySelector('[data-kv-mega-toggle]');
    var panel = scope.querySelector('[data-kv-mega]');
    if (!toggle || !panel || !once(toggle, 'kvMega')) return;

    function close() {
      if (panel.hidden) return;
      panel.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
    }

    function open() {
      panel.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
    }

    toggle.addEventListener('click', function (event) {
      event.preventDefault();
      if (panel.hidden) open(); else close();
    });

    document.addEventListener('click', function (event) {
      if (panel.hidden) return;
      if (panel.contains(event.target) || toggle.contains(event.target)) return;
      close();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || panel.hidden) return;
      close();
      toggle.focus();
    });

    panel.addEventListener('keydown', function (event) {
      if (event.key === 'Tab') trapFocus(panel, event);
    });
  }


  /* 5. Mobile drawer
     ---------------------------------------------------------------------- */

  function initDrawer(root) {
    var scope = root || document;
    var drawer = scope.querySelector('[data-kv-drawer]');
    var openBtn = scope.querySelector('[data-kv-drawer-open]');
    if (!drawer || !openBtn || !once(drawer, 'kvDrawer')) return;

    var closeBtns = drawer.querySelectorAll('[data-kv-drawer-close]');
    var lastFocused = null;

    function open() {
      lastFocused = document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      openBtn.setAttribute('aria-expanded', 'true');
      lockScroll();

      var first = drawer.querySelector(FOCUSABLE);
      if (first) first.focus();
    }

    function close() {
      if (!drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      openBtn.setAttribute('aria-expanded', 'false');
      unlockScroll();
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    openBtn.addEventListener('click', function (event) {
      event.preventDefault();
      open();
    });

    Array.prototype.forEach.call(closeBtns, function (btn) {
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        close();
      });
    });

    drawer.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') { close(); return; }
      if (event.key === 'Tab') trapFocus(drawer, event);
    });

    // Accordion sub-lists inside the drawer
    var accordions = drawer.querySelectorAll('[data-kv-accordion]');
    Array.prototype.forEach.call(accordions, function (btn) {
      btn.addEventListener('click', function () {
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!expanded));
        var target = document.getElementById(btn.getAttribute('aria-controls'));
        if (target) target.hidden = expanded;
      });
    });
  }


  /* 6. Quantity stepper
     ---------------------------------------------------------------------- */

  function initQty(root) {
    var groups = (root || document).querySelectorAll('[data-kv-qty]');
    Array.prototype.forEach.call(groups, function (group) {
      if (!once(group, 'kvQty')) return;

      var input = group.querySelector('input');
      if (!input) return;

      group.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-kv-qty-step]');
        if (!btn) return;
        event.preventDefault();

        var step = Number(btn.getAttribute('data-kv-qty-step')) || 1;
        var min = Number(input.getAttribute('min') || 1);
        var next = (Number(input.value) || min) + step;

        input.value = Math.max(min, next);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }


  /* 7. Product page — gallery + variant resolution
     --------------------------------------------------------------------------
     Products here carry up to 14 variants across two options. The old template
     rendered a flat <select> with no JS, so price and availability never
     changed. Prices arrive pre-formatted from Liquid, so there is no money
     formatting to reimplement here.
     ---------------------------------------------------------------------- */

  function initProduct(root) {
    var scope = root || document;
    var section = scope.querySelector('[data-kv-product]');
    if (!section || !once(section, 'kvProduct')) return;

    var mainImage = section.querySelector('[data-kv-main-image]');
    var thumbs = Array.prototype.slice.call(section.querySelectorAll('[data-kv-thumb]'));

    function selectThumb(btn) {
      thumbs.forEach(function (t) { t.classList.toggle('is-active', t === btn); });
    }

    thumbs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var src = btn.getAttribute('data-image');
        if (mainImage && src) {
          mainImage.src = src;
          mainImage.removeAttribute('srcset');
        }
        selectThumb(btn);
      });
    });

    // --- Variants ---
    var dataEl = section.querySelector('[data-kv-variant-data]');
    if (!dataEl) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    if (!variants || !variants.length) return;

    var idInput = section.querySelector('[data-kv-variant-id]');
    var priceEl = section.querySelector('[data-kv-price]');
    var compareEl = section.querySelector('[data-kv-compare]');
    var saleEl = section.querySelector('[data-kv-sale]');
    var addBtn = section.querySelector('[data-kv-add]');
    var addLabel = section.querySelector('[data-kv-add-label]');

    var optionButtons = Array.prototype.slice.call(section.querySelectorAll('[data-kv-option-button]'));
    var optionSelects = Array.prototype.slice.call(section.querySelectorAll('[data-kv-option-select]'));

    var optionCount = variants[0].options.length;

    function currentSelection() {
      var selection = [];
      var i;
      for (i = 0; i < optionCount; i++) selection[i] = null;

      optionButtons.forEach(function (btn) {
        if (btn.classList.contains('is-selected')) {
          selection[Number(btn.getAttribute('data-option-index'))] = btn.getAttribute('data-value');
        }
      });

      optionSelects.forEach(function (sel) {
        selection[Number(sel.getAttribute('data-option-index'))] = sel.value;
      });

      return selection;
    }

    function findVariant(selection) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var match = true;
        for (var j = 0; j < optionCount; j++) {
          if (selection[j] !== null && v.options[j] !== selection[j]) { match = false; break; }
        }
        if (match) return v;
      }
      return null;
    }

    // Grey out option values that pair with nothing available
    function markUnavailable(selection) {
      optionButtons.forEach(function (btn) {
        var index = Number(btn.getAttribute('data-option-index'));
        var probe = selection.slice();
        probe[index] = btn.getAttribute('data-value');

        var reachable = variants.some(function (v) {
          for (var j = 0; j < optionCount; j++) {
            if (probe[j] !== null && v.options[j] !== probe[j]) return false;
          }
          return v.available;
        });

        btn.classList.toggle('is-unavailable', !reachable);
      });
    }

    function apply() {
      var selection = currentSelection();
      var variant = findVariant(selection);

      markUnavailable(selection);

      if (!variant) {
        if (addBtn) addBtn.disabled = true;
        if (addLabel) addLabel.textContent = 'Unavailable';
        return;
      }

      if (idInput) idInput.value = variant.id;
      if (priceEl) priceEl.textContent = variant.price;

      if (compareEl) {
        compareEl.textContent = variant.compareAtPrice;
        compareEl.hidden = !variant.hasCompare;
      }
      if (saleEl) saleEl.hidden = !variant.hasCompare;

      if (addBtn) addBtn.disabled = !variant.available;
      if (addLabel) addLabel.textContent = variant.available ? 'Add to cart' : 'Sold out';

      if (variant.image && mainImage) {
        mainImage.src = variant.image;
        mainImage.removeAttribute('srcset');
        var match = thumbs.filter(function (t) {
          return t.getAttribute('data-media-id') === String(variant.mediaId);
        })[0];
        if (match) selectThumb(match);
      }

      // Keep the URL shareable without reloading
      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    optionButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var index = btn.getAttribute('data-option-index');
        optionButtons.forEach(function (other) {
          if (other.getAttribute('data-option-index') !== index) return;
          var isThis = other === btn;
          other.classList.toggle('is-selected', isThis);
          other.setAttribute('aria-checked', String(isThis));
        });
        apply();
      });
    });

    optionSelects.forEach(function (sel) {
      sel.addEventListener('change', apply);
    });

    apply();
  }



  /* 8. Announcement bar
     --------------------------------------------------------------------------
     Rotates messages and remembers a dismissal. The bar hides itself via a
     small inline script in the section so a dismissed bar never flashes; this
     controller only handles rotation and the close button.
     ---------------------------------------------------------------------- */

  function initAnnounce(root) {
    var bar = (root || document).querySelector('[data-kv-announce]');
    // NOT 'kvAnnounce': once() writes dataset[key], which would overwrite the
    // data-kv-announce attribute holding this bar's storage key.
    if (!bar || !once(bar, 'kvAnnounceBound')) return;

    var closeBtn = bar.querySelector('[data-kv-announce-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        bar.hidden = true;
        try {
          window.localStorage.setItem(bar.getAttribute('data-kv-announce') || 'kynvera:announce', 'dismissed');
        } catch (e) {
          /* Storage blocked — the bar still closes for this pageview. */
        }
      });
    }

    var items = Array.prototype.slice.call(bar.querySelectorAll('[data-kv-announce-item]'));
    if (items.length < 2 || REDUCED_MOTION) return;

    var speed = parseInt(bar.getAttribute('data-kv-announce-speed'), 10) || 5000;
    var index = 0;

    window.setInterval(function () {
      items[index].classList.remove('is-active');
      index = (index + 1) % items.length;
      items[index].classList.add('is-active');
    }, speed);
  }


  /* 9. Hero carousel
     --------------------------------------------------------------------------
     Moved out of the section's own IIFE. It previously registered a second,
     duplicate `shopify:section:load` listener; it now boots through the same
     init() path as every other controller.
     ---------------------------------------------------------------------- */

  function initHero(root) {
    var hero = (root || document).querySelector('[data-kv-hero]');
    if (!hero || !once(hero, 'kvHero')) return;

    var slides = Array.prototype.slice.call(hero.querySelectorAll('[data-kv-slide]'));
    var dots = Array.prototype.slice.call(hero.querySelectorAll('[data-kv-dot]'));
    var fills = Array.prototype.slice.call(hero.querySelectorAll('[data-kv-dot-fill]'));
    var prevBtn = hero.querySelector('[data-kv-prev]');
    var nextBtn = hero.querySelector('[data-kv-next]');

    if (slides.length <= 1) return;

    var autoplay = hero.getAttribute('data-autoplay') === 'true' && !REDUCED_MOTION;
    var speed = parseInt(hero.getAttribute('data-autoplay-speed'), 10) || 5000;
    var current = 0;
    var timer = null;
    var rafId = null;
    var startedAt = 0;

    function paintProgress(ratio) {
      if (!fills[current]) return;
      fills[current].style.setProperty('--kv-progress', (ratio * 100).toFixed(2) + '%');
    }

    function clearProgress() {
      fills.forEach(function (f) { f.style.setProperty('--kv-progress', '0%'); });
    }

    function step() {
      if (!autoplay) return;
      var ratio = Math.min((Date.now() - startedAt) / speed, 1);
      paintProgress(ratio);
      rafId = window.requestAnimationFrame(step);
    }

    function stop() {
      if (timer) { window.clearTimeout(timer); timer = null; }
      if (rafId) { window.cancelAnimationFrame(rafId); rafId = null; }
    }

    function start() {
      stop();
      if (!autoplay) return;
      startedAt = Date.now();
      clearProgress();
      timer = window.setTimeout(function () { go(current + 1); }, speed);
      rafId = window.requestAnimationFrame(step);
    }

    function go(next) {
      var target = (next + slides.length) % slides.length;
      if (target === current) return;

      slides[current].classList.remove('is-active');
      slides[current].setAttribute('aria-hidden', 'true');
      if (dots[current]) {
        dots[current].classList.remove('is-active');
        dots[current].setAttribute('aria-selected', 'false');
      }

      slides[target].classList.add('is-active');
      slides[target].setAttribute('aria-hidden', 'false');
      if (dots[target]) {
        dots[target].classList.add('is-active');
        dots[target].setAttribute('aria-selected', 'true');
      }

      current = target;
      start();
    }

    if (nextBtn) nextBtn.addEventListener('click', function () { go(current + 1); });
    if (prevBtn) prevBtn.addEventListener('click', function () { go(current - 1); });

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { go(i); });
    });

    hero.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') { go(current - 1); }
      else if (event.key === 'ArrowRight') { go(current + 1); }
    });

    hero.addEventListener('mouseenter', stop);
    hero.addEventListener('mouseleave', start);
    hero.addEventListener('focusin', stop);
    hero.addEventListener('focusout', start);

    // Pause while the tab is hidden — a backgrounded timer just burns through
    // every slide and lands the shopper somewhere arbitrary on return.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else start();
    });

    var touchStartX = 0;
    hero.addEventListener('touchstart', function (event) {
      touchStartX = event.changedTouches[0].screenX;
    }, { passive: true });

    hero.addEventListener('touchend', function (event) {
      var delta = event.changedTouches[0].screenX - touchStartX;
      if (Math.abs(delta) > 40) go(delta < 0 ? current + 1 : current - 1);
    }, { passive: true });

    start();
  }


  /* 10. Countdown
     --------------------------------------------------------------------------
     Reads an ISO datetime from data-end. An end time in the past (or an
     unparseable one) hides the timer and reveals the section's "ended" note,
     rather than counting down into negative numbers.
     ---------------------------------------------------------------------- */

  function pad(n) { return n < 10 ? '0' + n : String(n); }

  function initCountdown(root) {
    var nodes = (root || document).querySelectorAll('[data-kv-countdown]');

    Array.prototype.forEach.call(nodes, function (node) {
      if (!once(node, 'kvCountdown')) return;

      var end = Date.parse(node.getAttribute('data-end'));
      var scope = node.closest('[data-kv-deal]') || document;
      var endedNote = scope.querySelector('[data-kv-countdown-ended]');
      var timer = null;

      var out = {
        days: node.querySelector('[data-kv-cd="days"]'),
        hours: node.querySelector('[data-kv-cd="hours"]'),
        minutes: node.querySelector('[data-kv-cd="minutes"]'),
        seconds: node.querySelector('[data-kv-cd="seconds"]')
      };

      function finish() {
        if (timer) { window.clearInterval(timer); timer = null; }
        node.hidden = true;
        if (endedNote) endedNote.hidden = false;
      }

      function tick() {
        var diff = end - Date.now();
        if (!(diff > 0)) { finish(); return; }

        var total = Math.floor(diff / 1000);
        if (out.days) out.days.textContent = pad(Math.floor(total / 86400));
        if (out.hours) out.hours.textContent = pad(Math.floor((total % 86400) / 3600));
        if (out.minutes) out.minutes.textContent = pad(Math.floor((total % 3600) / 60));
        if (out.seconds) out.seconds.textContent = pad(total % 60);
      }

      if (isNaN(end)) { finish(); return; }

      tick();
      if (!node.hidden) timer = window.setInterval(tick, 1000);
    });
  }


  /* 11. Wishlist
     --------------------------------------------------------------------------
     localStorage-backed, no app and no customer account. Every read and write
     is guarded: Safari in private mode throws on access, and an exception here
     would take out the whole product grid.
     ---------------------------------------------------------------------- */

  var WISHLIST_KEY = 'kynvera:wishlist';

  function readWishlist() {
    try {
      var raw = window.localStorage.getItem(WISHLIST_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(function (h) { return typeof h === 'string' && h.length > 0; });
    } catch (e) {
      return [];
    }
  }

  function writeWishlist(list) {
    try {
      window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
      return true;
    } catch (e) {
      return false;
    }
  }

  function updateWishlistCount() {
    var count = readWishlist().length;
    var nodes = document.querySelectorAll('[data-kv-wishlist-count]');
    Array.prototype.forEach.call(nodes, function (node) {
      node.textContent = count;
      node.hidden = count < 1;
    });
  }

  function syncWishlistButtons(root) {
    var saved = readWishlist();
    var buttons = (root || document).querySelectorAll('[data-kv-wishlist]');
    Array.prototype.forEach.call(buttons, function (btn) {
      var on = saved.indexOf(btn.getAttribute('data-handle')) !== -1;
      btn.setAttribute('aria-pressed', String(on));
    });
  }

  function initWishlist(root) {
    syncWishlistButtons(root);
    updateWishlistCount();
  }

  document.addEventListener('click', function (event) {
    var btn = event.target.closest && event.target.closest('[data-kv-wishlist]');
    if (!btn) return;
    event.preventDefault();

    var handle = btn.getAttribute('data-handle');
    if (!handle) return;

    var list = readWishlist();
    var at = list.indexOf(handle);
    if (at === -1) list.push(handle); else list.splice(at, 1);

    if (!writeWishlist(list)) return;

    syncWishlistButtons(document);
    updateWishlistCount();
  });


  /* 12. Wishlist page
     --------------------------------------------------------------------------
     Cards are pulled through the Section Rendering API rather than rebuilt in
     JS: requesting /products/<handle>?section_id=... renders that section in
     the product's own context, so the saved items go through exactly the same
     kynvera-product-card snippet as every other grid and can never drift from
     it. Handles that 404 (deleted or unpublished) are pruned from storage.
     ---------------------------------------------------------------------- */

  function initWishlistPage(root) {
    var page = (root || document).querySelector('[data-kv-wishlist-page]');
    if (!page || !once(page, 'kvWishlistPage')) return;

    var grid = page.querySelector('[data-kv-wishlist-grid]');
    var empty = page.querySelector('[data-kv-wishlist-empty]');
    var loading = page.querySelector('[data-kv-wishlist-loading]');
    var clearBtn = page.querySelector('[data-kv-wishlist-clear]');
    var sectionId = page.getAttribute('data-card-section');

    function showEmpty() {
      if (loading) loading.hidden = true;
      if (grid) grid.hidden = true;
      if (empty) empty.hidden = false;
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        writeWishlist([]);
        updateWishlistCount();
        if (grid) grid.innerHTML = '';
        showEmpty();
      });
    }

    var handles = readWishlist();
    if (!handles.length) { showEmpty(); return; }

    var root_url = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    Promise.all(handles.map(function (handle) {
      return fetch(root_url + 'products/' + encodeURIComponent(handle) + '?section_id=' + sectionId, {
        headers: { 'Accept': 'text/html' }
      })
        .then(function (r) { return r.ok ? r.text() : null; })
        .then(function (html) { return html ? { handle: handle, html: html } : null; })
        .catch(function () { return null; });
    })).then(function (results) {
      var alive = results.filter(Boolean);

      // Drop anything that no longer resolves so the list self-heals.
      if (alive.length !== handles.length) {
        writeWishlist(alive.map(function (r) { return r.handle; }));
        updateWishlistCount();
      }

      if (!alive.length) { showEmpty(); return; }

      grid.innerHTML = alive.map(function (r) {
        return '<li>' + r.html + '</li>';
      }).join('');

      if (loading) loading.hidden = true;
      if (empty) empty.hidden = true;
      grid.hidden = false;

      syncWishlistButtons(grid);
    });
  }


  /* Boot
     ---------------------------------------------------------------------- */

  function init(root) {
    initReveal(root);
    initHeader(root);
    initMega(root);
    initDrawer(root);
    initQty(root);
    initProduct(root);
    initAnnounce(root);
    initHero(root);
    initCountdown(root);
    initWishlist(root);
    initWishlistPage(root);
  }

  document.documentElement.classList.remove('no-js');

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Theme editor: re-init whatever section just reloaded.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function () {
    document.body.classList.remove('kv-scroll-locked');
    document.body.style.top = '';
  });
})();
